import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, X, Trash2, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { LoadError, errMessage } from "@/components/LoadError";
import { OpvolgerAvatar } from "@/components/afspraken/Opvolger";
import { GROEP, groepVan, relLabel, eenRegel } from "@/components/afspraken/afsprakenUtils";
import { StatusPill } from "@/components/afspraken/AfspraakRow";
import { KpiChips, RedenChips, VolgendeRegel } from "./NuParts";
import { Strook } from "./Strook";
import { DossierBezoeken } from "./DossierBezoeken";
import { weekNr, kortDatum, cap, callTekst } from "./overzichtUtils";

const Kaart = ({ titel, sub, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-zinc-200 rounded-[10px]">
    <header className="px-4 py-2.5 border-b border-zinc-200 flex items-baseline gap-2">
      <h3 className="font-heading font-black uppercase text-xs tracking-wider text-zinc-900">{titel}</h3>
      {sub && <span className="text-xs text-zinc-400">{sub}</span>}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const StatusKop = ({ s }) => {
  // R13: volgt de status van de server (zelfde als de tegel), rekent niets zelf uit.
  const [txt, cls] = s.rood ? [s.roodZonderAfspraak ? "Rood zonder afspraak" : "Rood", "bg-rose-600 text-white"]
    : s.status === "geen-call" ? ["Nog geen call", "bg-zinc-200 text-zinc-700"]
    : s.status === "aandacht" ? ["Aandacht nodig", "bg-amber-400 text-zinc-950"]
    : s.status === "nog-te-bellen" ? ["Nog te bellen", "bg-blue-600 text-white"] : ["Op koers", "bg-emerald-500 text-white"];
  return <span data-testid="dossier-status" className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
};

const LogKaart = ({ d, onAdd, onDelete, busy }) => {
  const { user } = useAuth();
  const [tekst, setTekst] = useState("");
  const submit = async (e) => { e.preventDefault(); if (await onAdd(tekst)) setTekst(""); };
  return (
    <Kaart titel="Wat speelt er" testid="dossier-wat-speelt-er">
      <form onSubmit={submit} className="flex gap-2 mb-3">
        <input data-testid="dossier-log-input" value={tekst} maxLength={500} onChange={(e) => setTekst(e.target.value)} placeholder="Ontwikkeling toevoegen…"
          className="flex-1 min-w-0 px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <button data-testid="dossier-log-toevoegen" disabled={busy || !tekst.trim()} className="px-3 py-2 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 disabled:opacity-40">Toevoegen</button>
      </form>
      {d.log.length === 0 ? <p className="text-sm text-zinc-400 italic">Nog niets vastgelegd.</p> : (
        <ul className="space-y-3">
          {d.log.map((l) => (
            <li key={l.id} data-testid={`dossier-log-${l.id}`} className="flex gap-2.5">
              <OpvolgerAvatar name={l.door} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs"><span className="font-semibold capitalize text-zinc-800">{l.door}</span><span className="font-mono text-zinc-400">{kortDatum(l.timestamp)}</span>
                  {l.door === user.username && <button data-testid={`dossier-log-verwijder-${l.id}`} onClick={() => onDelete(l)} aria-label="Verwijderen" className="ml-auto text-zinc-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-line break-words">{l.tekst}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {d.logTotaal > d.log.length && <p className="text-xs text-zinc-400 mt-2">De laatste {d.log.length} van {d.logTotaal} worden getoond.</p>}
      <p className="text-xs text-zinc-400 mt-3">Iedereen ziet wie wat wanneer schreef. Toevoegen, nooit overschrijven.</p>
    </Kaart>
  );
};

const CallsKaart = ({ d }) => (
  <Kaart titel="Calls per week" sub="Sam en Daisy door elkaar" testid="dossier-calls">
    {d.calls.length === 0 ? <p className="text-sm text-zinc-400 italic">Geen calls in de laatste 12 weken.</p> : (
      <ol className="border-l-2 border-zinc-200 ml-2 space-y-4">
        {d.calls.map((c) => (
          <li key={`${c.week}-${c.om}`} data-testid={`dossier-call-${c.week}-${c.om}`} className="pl-4 relative">
            <span data-testid={`dossier-call-stip-${c.week}-${c.om}`} className={`absolute -left-[7px] top-1 h-3 w-3 rounded-full ${c.rood ? "bg-rose-500" : (c.cijfers?.beantwoord ?? 0) < 4 ? "bg-amber-400" : "bg-emerald-500"}`} />
            <div className="flex flex-wrap items-center gap-2">
              <span data-testid={`dossier-call-kop-${c.week}-${c.om}`} className="text-sm font-bold">{callTekst(c) || `Week ${weekNr(c.week)}`} · <span className="capitalize">{c.om}</span>{c.omInactief && " (uit dienst)"}</span>
              <OpvolgerAvatar name={c.om} size="h-5 w-5 text-[10px]" />
              <KpiChips call={c} />
            </div>
            {(c.bevinding || c.verslagNotitie) && <p className="text-xs text-zinc-500 mt-1 line-clamp-3 whitespace-pre-line break-words">{c.bevinding || c.verslagNotitie}</p>}
            <Link data-testid={`dossier-call-link-${c.week}-${c.om}`} to={`/locatie-call?week=${c.week}&om=${c.om}&locatie=${encodeURIComponent(d.locatie)}`} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 mt-1">
              Bekijk in Locatie-call <ExternalLink className="h-3 w-3" />
            </Link>
          </li>
        ))}
      </ol>
    )}
    <p className="text-xs text-zinc-400 mt-3">Lezen mag iedere OM; bewerken alleen wie de call deed.</p>
  </Kaart>
);

const AfspraakLink = ({ a }) => (
  <Link to={`/afspraken?select=${a.id}`} data-testid={`dossier-afspraak-${a.id}`} className="flex items-center gap-2.5 py-2 hover:bg-zinc-50 -mx-2 px-2 rounded">
    {a.status === "open" ? <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${GROEP[groepVan(a)].dot}`} /> : <StatusPill status={a.status} />}
    <span className="flex-1 min-w-0 text-sm text-zinc-800 truncate">{eenRegel(a.wat)}</span>
    <OpvolgerAvatar name={a.opvolgerEffectief} size="h-5 w-5 text-[10px]" />
    {a.status === "open" && <span className={`font-mono text-xs whitespace-nowrap ${a.overdue ? "text-rose-600 font-bold" : "text-zinc-500"}`}>{relLabel(a)}</span>}
  </Link>
);

export default function LocatieDossier({ locatie, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [wis, setWis] = useState(null);
  const [afgerondOpen, setAfgerondOpen] = useState(false);

  // R18: verzoek-guard — een laat antwoord voor A vult nooit het paneel van B.
  const reqRef = React.useRef(0);
  const load = useCallback(() => {
    if (!locatie) return;
    const id = ++reqRef.current;
    api.get(`/locaties/${encodeURIComponent(locatie)}/dossier`)
      .then((r) => { if (id === reqRef.current) { setD(r.data); setErr(""); } })
      .catch((e) => { if (id === reqRef.current) setErr(errMessage(e)); });
  }, [locatie]);
  useEffect(() => { setD(null); setErr(""); setAfgerondOpen(false); load(); }, [load]);

  const add = async (tekst) => {
    setBusy(true);
    try {
      await api.post(`/locaties/${encodeURIComponent(locatie)}/log`, { tekst });
      toast.success("Ontwikkeling toegevoegd"); load(); onChanged(); return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Toevoegen mislukt"); return false; } finally { setBusy(false); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/locaties/${encodeURIComponent(locatie)}/log/${wis.id}`);
      toast.success("Ontwikkeling verwijderd"); setWis(null); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };

  const s = d?.status;
  return (
    <Sheet open={!!locatie} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" data-testid="locatie-dossier" className="w-full sm:max-w-2xl p-0 overflow-y-auto bg-zinc-100 [&>button:last-child]:hidden">
        <SheetTitle className="sr-only">Dossier {locatie}</SheetTitle>
        <SheetDescription className="sr-only">Calls, afspraken en ontwikkelingen van de locatie</SheetDescription>
        <div className="bg-zinc-950 text-white px-5 py-4 flex items-center gap-3 sticky top-0 z-10">
          <MapPin className="h-5 w-5 text-blue-400 shrink-0" />
          <h2 className="font-heading font-black text-xl truncate" data-testid="dossier-titel">{locatie}</h2>
          {s && <StatusKop s={s} />}
          <button data-testid="dossier-sluiten" onClick={onClose} aria-label="Sluiten" className="ml-auto h-8 w-8 rounded-md flex items-center justify-center text-zinc-300 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        {err && <div className="p-4"><LoadError error={err} onRetry={load} testid="dossier-load-error" /></div>}
        {!d && !err && <p className="p-8 text-center text-sm text-zinc-400">Laden…</p>}
        {d && (
          <>
            {s.reeks?.length > 0 && (
              <div className="bg-white border-b border-zinc-200 px-5 py-3 space-y-2" data-testid="dossier-reeks">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">13 weken · over week</div>
                <Strook r={s} groot testid="dossier-strook" />
                <p data-testid="dossier-reeks-regel" className="text-sm text-zinc-700">
                  {s.roodAantal} van {s.reeks.length} weken rood · <span className={s.open === 0 && (s.rood || s.terugkerend) ? "text-rose-600 font-semibold" : ""}>{s.open} open {s.open === 1 ? "afspraak" : "afspraken"}</span>
                  {s.terugkerend && <span className="text-rose-700 font-semibold"> · terugkerend ({s.roodOpRij ? "twee weken op rij" : "vier of meer van de dertien"})</span>}
                </p>
              </div>
            )}
            <div className="bg-white border-b border-zinc-200 px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid="dossier-feiten">
              <span className="inline-flex items-center gap-1">{d.oms.length ? d.oms.map((o) => <OpvolgerAvatar key={o} name={o} />) : <span className="text-zinc-400 text-xs">geen OM toegewezen</span>}</span>
              <span data-testid="dossier-call-telt">{s.laatsteCall ? `${callTekst(s.laatsteCall)} · ${cap(s.laatsteCall.om)}${s.bezig ? ` · bezig ${s.bezig.beantwoord}/${s.bezig.totaal}` : ""}` : "Nog geen call"}</span>
              {(s.redenen || []).length > 0 && <RedenChips r={s} />}
              <span className="font-mono">{s.open} open</span>
              {s.teLaat > 0 && <span className="font-mono text-rose-600 font-bold">{s.teLaat} te laat</span>}
              <div className="basis-full"><VolgendeRegel r={s} /></div>
            </div>
            <div className="p-4 space-y-4">
              <LogKaart d={d} onAdd={add} onDelete={setWis} busy={busy} />
              <CallsKaart d={d} />
              <DossierBezoeken d={d} onChanged={load} />
              <Kaart titel="Open afspraken" sub={`${d.openAfspraken.length}`} testid="dossier-open-afspraken">
                {d.openAfspraken.length === 0 ? <p className="text-sm text-zinc-400 italic">Geen open afspraken.</p> : d.openAfspraken.map((a) => <AfspraakLink key={a.id} a={a} />)}
              </Kaart>
              <section className="bg-white border border-zinc-200 rounded-[10px]" data-testid="dossier-recent-afgerond">
                <button onClick={() => setAfgerondOpen((v) => !v)} data-testid="dossier-recent-afgerond-toggle" className="w-full px-4 py-2.5 flex items-center gap-2 text-left">
                  {afgerondOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="font-heading font-black uppercase text-xs tracking-wider">Recent afgerond</span>
                  <span className="text-xs text-zinc-400">{d.recentAfgerond.length}</span>
                </button>
                {afgerondOpen && <div className="px-4 pb-3">{d.recentAfgerond.length === 0 ? <p className="text-sm text-zinc-400 italic">Nog niets afgerond.</p> : d.recentAfgerond.map((a) => <AfspraakLink key={a.id} a={a} />)}</div>}
              </section>
            </div>
          </>
        )}
        <AlertDialog open={!!wis} onOpenChange={(o) => { if (!o) setWis(null); }}>
          <AlertDialogContent data-testid="dossier-log-verwijder-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Ontwikkeling verwijderen?</AlertDialogTitle>
              <AlertDialogDescription>"{wis?.tekst}" wordt verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction data-testid="dossier-log-verwijder-bevestig" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Verwijderen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
